import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button.js';
import infoIcon from '../assets/info-icon.svg';

/**
 * "High Security must be completed" block panel (issue #363).
 *
 * The Mobile Number Maintenance screen (SCRN-26 / CCF-6) is a servicing action
 * that must not be performed until the customer has passed **High Security**
 * during the (online) interaction. @dylan219 ruled (2026-07-15) that reaching it
 * before High Security is complete must be BLOCKED with this panel — a verbatim
 * message plus two affordances:
 *   - a **Back** button that returns the agent to the page they came from;
 *   - a **Go to High Security** button that routes to the High Security screen
 *     (EDS High, SCRN-07 — the personal-customer high-security page, per the
 *     @dylan219 #101 ruling that "High Risk Security" for the EDS journey opens
 *     EDS High, not VISA High).
 *
 * This mirrors the existing client-side block-page patterns — the #248
 * {@link PermissionDeniedPage} (RBAC view denial, Back button) and the #222
 * {@link ServicingVerificationRequired} (medium-verification, return-to-security
 * button). Like those, it is a UX affordance: the authoritative write is gated
 * SERVER-SIDE. The BFF verification ladder now includes `high`
 * (`none → pan → medium → high`); the BFF records `high` server-side via
 * `/security/high/complete` (backed by an active High Security run whose 2FA step
 * was satisfied server-side — an OTP validation, or a sanctioned skip recorded
 * via `/security/high/skip-2fa`), and `POST /api/customers/contact-number` is
 * `requireVerification('high')` / `requireVerifiedHigh`-gated, which is never
 * relaxed. This screen-access panel is the matching client-side affordance.
 */
export function HighSecurityRequired(): React.JSX.Element {
  const navigate = useNavigate();

  const goBack = (): void => {
    // Return the agent to the page they came from. If there is no history to go
    // back to (deep-link / first entry) `navigate(-1)` would dead-end, so fall
    // back to the app root — mirrors {@link PermissionDeniedPage}.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <main
      className="max-w-[900px] mx-auto px-12 py-12"
      aria-labelledby="high-security-required-heading"
    >
      <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
        <img
          src={infoIcon}
          alt=""
          aria-hidden="true"
          className="w-5 h-5 mt-1 shrink-0"
        />
        <div>
          <h1
            id="high-security-required-heading"
            role="alert"
            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4"
          >
            High Security must be completed to access this journey
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              onClick={goBack}
              aria-label="Back"
              className="bg-white text-[#1D252C] border-2 border-[#979C9F] hover:bg-[#f3f0ec] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/security/eds-high')}
              aria-label="Go to High Security"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Go to High Security
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
